import * as FormsUI from '../forms-ui-form-faas.interface';
import { FormSubmissionPayload, FormSubmissionConsent } from '../forms-ui-form-payload.interface';
import { INucaptchaService } from './nucaptcha-service';
import { IFaaSDataClient } from '../api/faas-data';
import { IConsentService } from './consent-service';

/** exported for testing */
/** source: https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates */
export const isCheckboxElement = (elem: HTMLFormsUiCheckboxElement | FormsUI.AnyFormElement):
	elem is HTMLFormsUiCheckboxElement => (elem as HTMLFormsUiCheckboxElement).checked !== undefined;

/* eslint-disable-next-line @stencil-community/ban-exported-const-enums */
export enum SubmissionStatus {
  Success,
  Error,
  NucaptchaRequired,
}

export interface IFormSubmissionService {
  submitToPardot(
    pardotActionUrl: string,
    formsUiForm: HTMLFormElement,
    formRefs: FormsUI.AnyFormElement[]): Promise<SubmissionStatus>;

  submitToDb(formRefs: FormsUI.AnyFormElement[]): Promise<SubmissionStatus>
}

export class FormSubmissionService implements IFormSubmissionService {
	private nucaptchaService: INucaptchaService;

	private consentService: IConsentService;

	private faasDataClient: IFaaSDataClient;

	private formData: FormsUI.FormData;

	private testMode: boolean;

	constructor(
		nucaptchaService: INucaptchaService,
		consentService: IConsentService,
		faasDataClient: IFaaSDataClient,
		formData: FormsUI.FormData,
		testMode: boolean,
	) {
		this.nucaptchaService = nucaptchaService;
		this.consentService = consentService;
		this.faasDataClient = faasDataClient;
		this.formData = formData;
		this.testMode = testMode;
	}

	/** Submits a FaaS form to Pardot */
	async submitToPardot(
		pardotActionUrl: string,
		formsUiForm: HTMLFormElement,
		formRefs: FormsUI.AnyFormElement[],
	): Promise<SubmissionStatus> {
		const preparedConsents = this.consentService.prepareConsentsForSubmission();

		/** We submit the form to the database for validation purposes. The server does not store the form data */
		const submitStatus: SubmissionStatus = await this.submitFormInternal(formRefs, preparedConsents);
		if (submitStatus !== SubmissionStatus.Success) {
			return submitStatus;
		}

		/** If the form is in test mode, we will not submit to Pardot */
		if (this.testMode) {
			return SubmissionStatus.Success;
		}

		/*
		 * If validation succeeds, proceed to submit to Pardot
		 * Pardot accepts consents as hidden form fields named consentDetails-n
		 * where n increases with each consent included consent.
		 */
		preparedConsents.forEach((consent: FormSubmissionConsent, i: number) => {
			const consentDetailsField = document.createElement('input');
			consentDetailsField.type = 'hidden';
			consentDetailsField.name = `consentDetails-${i}`;
			consentDetailsField.value = JSON.stringify(consent);
			formsUiForm.appendChild(consentDetailsField);
		});

		try {
			/**
			 * Make API call to Pardot using serialized form data
			 * NOTE: According to the MDN docs
			 * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams#options
			 * FormData __is__ a valid type to pass into the constructor for URLSearchParams.
			 * This solution is easier than manually scanning the form and building a k-v object
			 */
			const formData = new FormData(formsUiForm);

			// @ts-ignore
			const searchParams = new URLSearchParams(formData);

			const pardotReq = await fetch(
				pardotActionUrl,
				{
					// no-referrer is used to force Pardot to send the error state in the body of the response
					referrerPolicy: 'no-referrer',
					method: 'POST',
					body: searchParams,
					/*
					 * This normally reads "manual"
					 * See: https://confluence.mastercard.int/pages/viewpage.action?pageId=1685338424
					 */
					redirect: 'follow',
					/*
					 * This line normally doesn't exist.
					 * See: https://confluence.mastercard.int/pages/viewpage.action?pageId=1685338424
					 */
					mode: 'no-cors',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
				},
			);

			// first, check if a major issue occured (4xx,5xx errors)
			if (pardotReq.status > 399) {
				return SubmissionStatus.Error;
			}

			/**
			 * We require that users' Pardot configurations use
			 * "referrer" as the error redirect type.
			 * This ensures that we correctly capture the error returned and submit the error as an event
			 */
			/*
			 *
			 * This code is temporarily disabled.
			 * See https://confluence.mastercard.int/pages/viewpage.action?pageId=1685338424
			 *
			 * const reqBody = await pardotReq.text();
			 * if (reqBody.length > 0 && reqBody.includes('Please correct the following errors:')) {
			 * 	return SubmissionStatus.Error;
			 * }
			 */
			/**
			 * Response did not contain an error body,
			 * therefore we can assume Pardot redirect indicates success
			 *
			 * NOTE: If a user configures their Pardot form to use a "custom URL" for errors,
			 * we won't be able to catch the error state here and will proceed as if the request was successful.
			 * The reason for this behavior is that we've opted to NOT follow 302s,
			 * which prevents us from viewing the headers of the server response.
			 */
			return SubmissionStatus.Success;
		} catch (error) {
			// general failure in API call
			// eslint-disable-next-line no-console
			console.error(error);
			/*
			 * This normally reads return SubmissionStatus.Error;
			 * See: https://confluence.mastercard.int/pages/viewpage.action?pageId=1685338424
			 */
			return SubmissionStatus.Success;
		}
	}

	/** Submits a FaaS form to the FaaS Database */
	async submitToDb(formRefs: FormsUI.AnyFormElement[]): Promise<SubmissionStatus> {
		const preparedConsents = this.consentService.prepareConsentsForSubmission();
		return this.submitFormInternal(formRefs, preparedConsents);
	}

	/*
	 * Internal function for handling submission to the FaaS API. This is used for db submission
	 * and for pardot submission (for the purpose of validation)
	 */
	async submitFormInternal(
		formRefs: FormsUI.AnyFormElement[],
		consents: FormSubmissionConsent[],
	): Promise<SubmissionStatus> {
		if (this.formData.state === 'PUBLISHED') {
			/** prepare form fields for payload */
			const formFields = this.prepareFields(formRefs);

			/** prepare captcha details */
			const captchaDetails = await this.nucaptchaService.prepareCaptchaDetails();

			const { formId, applicationId, version } = this.formData;

			/** build payload */
			const payload: FormSubmissionPayload = {
				formId,
				applicationId,
				version,
				testMode: this.testMode,
				consents,
				formData: formFields,
				captchaDetails,
			};

			let jsonResponse: any;

			try {
				jsonResponse = await this.faasDataClient.submitForm(payload, this.testMode);
			} catch (error) {
				/* eslint-disable-next-line no-console */
				console.error(error);
				return SubmissionStatus.Error;
			}

			if (!jsonResponse.captchaDetails) return SubmissionStatus.Success;

			this.nucaptchaService.handleCaptchaResponse(jsonResponse.captchaDetails);
			return SubmissionStatus.NucaptchaRequired;
		} return undefined;
	}

	/*
	 * Builds a form repsonse payload for form submission. The return value
	 * is an object whose keys are form field names and values are the
	 * corresponding values form the form fields.
	 */
	prepareFields(formRefs: FormsUI.AnyFormElement[]): { [key: string]: (string | string[]) } {
		const fields: { [key: string]: (string | string[]) } = {};

		formRefs.forEach((formRef) => {
			if (isCheckboxElement(formRef)) {
				if (formRef.checked) {
					fields[formRef.name] = 'true';
				}
			} else {
				const { name, value } = formRef;
				fields[name] = value;
			}
		});
		return fields;
	}
}

export const createFormSubmissionService = (
	nucaptchaService: INucaptchaService,
	consentService: IConsentService,
	faasDataClient: IFaaSDataClient,
	formData: FormsUI.FormData,
	testMode: boolean,
): IFormSubmissionService => new FormSubmissionService(
	nucaptchaService,
	consentService,
	faasDataClient,
	formData,
	testMode,
);
